import { ApiError } from '@graal/api';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  classifyError,
  errorReference,
  problemDescription,
  shortReference,
} from '@/components/patterns/errors';
import i18n from '@/i18n';
import { reportClientError, type ClientErrorKind } from '@/lib/client-errors';

/**
 * Nouveaux essais : une LECTURE est retentée une fois — sauf refus, absence et fonction coupée,
 * qui ne changeront pas ; sur 503/504 (base occupée), passé le `Retry-After` (10 s au plus). Une
 * ÉCRITURE n'est jamais retentée d'office : la rejouer en boucle sur une base saturée l'aggrave, et
 * un POST rejoué peut créer deux fois. L'écran dit « réessayez » (`patterns.states.busy`).
 */
export const queryClientOptions = {
  queries: {
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: (failures: number, error: unknown) =>
      failures < 1 && !(error instanceof ApiError && [401, 403, 404, 501].includes(error.status)),
    retryDelay: (attempt: number, error: unknown) =>
      error instanceof ApiError && error.isBusy && error.retryAfterSeconds !== undefined
        ? Math.min(error.retryAfterSeconds, 10) * 1000
        : Math.min(1000 * 2 ** attempt, 10_000),
  },
  mutations: { retry: false },
} as const;

/**
 * `meta.errorToast` (requête ou mutation) : signaler l'échec par un toast traduit, avec la
 * référence de corrélation d'une erreur serveur. **Sur demande seulement**, pour ne rien doubler :
 * - une requête rend déjà son échec à l'écran (`ResourceState`, qui affiche lui-même la
 *   référence quand on lui passe `error`) ;
 * - une mutation se signale déjà elle-même (`onError` + `toast.error`, ou la bannière de
 *   `ResourceForm` / `Wizard`).
 * Le drapeau sert aux appels dont personne n'affiche l'échec (lecture d'arrière-plan, geste
 * « fire and forget »).
 */
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { errorToast?: boolean };
    mutationMeta: { errorToast?: boolean };
  }
}

/** Toast d'un échec signalé par `meta.errorToast` ; un 501 `feature_disabled` n'est pas une panne. */
export function reportError(error: unknown, id?: string): void {
  const kind = classifyError(error);
  if (kind === 'unavailable') return;
  const reference = errorReference(error);
  toast.error(problemDescription(i18n.t, kind, error), {
    ...(id ? { id } : {}),
    ...(reference
      ? { description: `${i18n.t('patterns.reference.label')} ${shortReference(reference)}` }
      : {}),
  });
}

/**
 * `report` : l'échec part aussi vers graal-api (`POST /client-errors`, `lib/client-errors.ts`), qui
 * ne garde que les pannes (5xx hors 501, réseau) et borne tout — débit, doublons, plafond par session.
 * Remplaçable dans les tests.
 */
export function createQueryClient(
  report: (kind: ClientErrorKind, error: unknown) => void = reportClientError,
): QueryClient {
  return new QueryClient({
    defaultOptions: queryClientOptions,
    queryCache: new QueryCache({
      onError: (error, query) => {
        report('query', error);
        // `id` = la requête : un échec répété (rafraîchissement périodique) remplace son toast.
        if (query.meta?.errorToast) reportError(error, `query:${query.queryHash}`);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        report('mutation', error);
        if (mutation.meta?.errorToast) reportError(error);
      },
    }),
  });
}
